import { isAxiosError } from 'axios';

import { injectAxios } from '@/lib/axios/Axios';
import { HubSpotPropertiesPayload } from '@/lib/hubspot/client';
import tokenStorageService from '@/utils/token.util';

type HubspotContactResponse =
  | { status: 'success'; contactId: string | null }
  | { status: 'error'; error: string };

type HubspotUpdateResponse = { status: 'success'; contactId: string; updated: true } | {
  status: 'error';
  error: string;
};

type HubspotCreateResponse = { status: 'success'; contactId: string; created: true } | {
  status: 'error';
  error: string;
};

class HubspotRequests {
  private static readonly axiosInstance = injectAxios('/api');

  private static async waitForToken(maxWaitMs = 3000, intervalMs = 100) {
    // In tests, skip waiting entirely to keep tests fast and deterministic
    if (process.env.NODE_ENV === 'test') return;

    const start = Date.now();
    let token = tokenStorageService.getToken();

    while (!token && Date.now() - start < maxWaitMs) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      token = tokenStorageService.getToken();
    }
  }

  private static async sleep(ms: number) {
    if (process.env.NODE_ENV === 'test') return;
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static async getContactIdByEmail(email: string, maxAttempts = 10): Promise<string | null> {
    const normalizedEmail = email?.trim();

    if (!normalizedEmail) return null;

    await this.waitForToken();

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const { data } = await this.axiosInstance.get<HubspotContactResponse>('/hubspot-contact', {
          params: { email: normalizedEmail },
        });

        if (data.status === 'success') {
          if (data.contactId) return data.contactId;
          if (attempt === maxAttempts) return null;
        } else {
          throw new Error(data.error || 'Unable to fetch HubSpot contact');
        }
      } catch (err: unknown) {
        const status = isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401 && attempt < maxAttempts) {
          await this.waitForToken(5000, 100);
          continue;
        }
        throw err;
      }

      await this.sleep(700);
    }

    return null;
  }

  static async createContactByEmail(
    email: string,
    properties?: HubSpotPropertiesPayload,
  ): Promise<string | null> {
    const normalizedEmail = email?.trim();

    if (!normalizedEmail) return null;

    await this.waitForToken();

    const payload = properties
      ? { email: normalizedEmail, properties }
      : { email: normalizedEmail };

    try {
      const { data } = await this.axiosInstance.put<HubspotCreateResponse>('/hubspot-contact', payload);

      if (data.status === 'success') {
        return data.contactId;
      }

      throw new Error(data.error || 'Unable to create HubSpot contact');
    } catch (err: unknown) {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) {
        await this.waitForToken(5000, 100);
        const { data } = await this.axiosInstance.put<HubspotCreateResponse>('/hubspot-contact', payload);
        if (data.status === 'error') {
          throw new Error(data.error || 'Unable to create HubSpot contact');
        }
        return data.contactId;
      }
      throw err;
    }
  }

  static async updateContactProperties(contactId: string, properties: HubSpotPropertiesPayload) {
    await this.waitForToken();

    try {
      const { data } = await this.axiosInstance.patch<HubspotUpdateResponse>('/hubspot-contact', {
        contactId,
        properties,
      });

      if (data.status === 'error') {
        throw new Error(data.error || 'Unable to update HubSpot contact properties');
      }
    } catch (err: unknown) {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) {
        await this.waitForToken(5000, 100);
        const { data } = await this.axiosInstance.patch<HubspotUpdateResponse>('/hubspot-contact', {
          contactId,
          properties,
        });
        if (data.status === 'error') throw new Error(data.error || 'Unable to update HubSpot contact properties');
        return;
      }
      throw err;
    }
  }
}

export default HubspotRequests;
